import { EditNote } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { action, computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { Fragment } from 'react';
import NumeroProcesso from '../NumeroProcesso';
import PainelFiltros from '../PainelFiltros';
import ReactComponent from '../ReactComponent';
import FiltroRapido from './FiltroRapido';
import PainelProcessosOperacoesEmLote from './PainelProcessosOperacoesEmLote';
import OperacoesEmLoteSelecionar from './OperacoesEmLoteSelecionar';
import ModalConfirmacao, { TIPO_CONFIRMACAO } from './ModalConfirmacao';
import AutoTexto from '@/components/shared/AutoTexto/AutoTexto';

class RascunhoCertidaoEmLote extends ReactComponent {
    dialogListagemOpen = false;
    dialogConfirmacaoOpen = false;
    rascunho = '';
    operacaoDecisao = 'MANTER';
    operacaoObservacao = 'SUBSTITUIR';

    constructor(props) {
        super(props);

        makeObservable(this, {
            dialogListagemOpen: observable,
            dialogConfirmacaoOpen: observable,
            rascunho: observable,
            operacaoDecisao: observable,
            operacaoObservacao: observable,
            handleClickIcone: action,
            handleCloseDialog: action,
            handleOpenDialogConfirmacao: action,
            handleCloseDialogConfirmacao: action,
            handleConfirmarEnvioRascunho: action,
            handleIncluirOpcaoSelecionada: action,
            desabilitarAlteracao: computed,
            limparProcessoPesquisa: action,
            processos: computed,
            processosSelecionados: computed,
            mensagemLoteSemProcessos: computed,
            opcoesAutoTexto: computed,
            handleRascunho: action,
            handleChangeOperacaoDecisao: action,
            handleChangeOperacaoObservacao: action,
            renderRascunho: computed,
            renderConteudo: computed
        });
    }

    handleClickIcone = async () => {
        this.rascunho = '';
        this.operacaoDecisao = 'MANTER';
        this.operacaoObservacao = 'SUBSTITUIR';
        this.dialogListagemOpen = true;
    };

    handleCloseDialog = () => {
        this.dialogListagemOpen = false;
    };

    handleOpenDialogConfirmacao = async () => {
        this.dialogConfirmacaoOpen = true;
    };

    handleCloseDialogConfirmacao = () => {
        this.dialogConfirmacaoOpen = false;
    };

    handleConfirmarEnvioRascunho = async () => {
        this.handleCloseDialogConfirmacao();
        await this.store.processarLote({
            titulo: 'Enviando rascunhos das certidões',
            indeterminate: true,
            qtdTotalProcessos: this.processosSelecionados.length,
            lotesProcessos: [this.processosSelecionados],
            funcaoOperacao: this.envioRascunhoEmLote
        });
        await this.store.recarregarListaProcessos();
    };

    handleIncluirOpcaoSelecionada = (texto) => {
        if (this.rascunho.indexOf(texto) !== -1) {
            this.mensagemStore.abrirMensagemAlerta('Observação já presente no texto.');
        } else {
            this.rascunho = `${this.rascunho}${this.rascunho.trim() === '' ? '' : '\n'}${texto}`;
        }
    };

    envioRascunhoEmLote = async () => {
        let resultado = await this.certidaoService.salvarRascunhoCertidaoEmLote(
            this.processosSelecionados.map((registro) => ({
                sessaoId: registro.sessaoId,
                numeroProcesso: registro.processo.numeroCompleto
            })),
            this.rascunho,
            this.operacaoDecisao,
            this.operacaoObservacao
        );
        this.store.atualizarProcessamentoLote(resultado);
        return resultado;
    };

    get desabilitarAlteracao() {
        if (!this.processosSelecionados || this.processosSelecionados.length === 0) {
            return true;
        }
        if (this.operacaoDecisao === 'MANTER') {
            return !this.isStringPreenchida(this.rascunho);
        }
        if (this.operacaoObservacao === 'ADICIONAR' && !this.isStringPreenchida(this.rascunho)) {
            return true;
        }
        return false;
    }

    limparProcessoPesquisa = () => {
        this.processoPesquisa = { numero: '', digito: '', ano: '', orgao: '', regional: '', vara: '' };
    };

    get processos() {
        return (
            this.store.opcoesProcessosEmExibicao
                .find((opcaoProcessos) => opcaoProcessos.id === 'P')
                .funcaoProcessosFiltrados()
                .filter((registro) => this.filtrarProcessoPeloNumero(registro)) || []
        );
    }

    get processosSelecionados() {
        return this.processosSelecionaveis.filter((registro) => this.store.isCheckedOperacoesEmLote(registro));
    }

    get processosSelecionaveis() {
        return this.processos.filter((registro) => !registro.certidaoGerada);
    }

    get mensagemLoteSemProcessos() {
        return 'Não há processos para alteração de rascunho de certidão em lote' + this.complementoMensagemFiltrando;
    }

    get opcoesAutoTexto() {
        return this.preSessaoStore.autoTexto;
    }

    handleRascunho = (event) => {
        this.rascunho = event.target.value;
    };

    handleChangeOperacaoDecisao = (event) => {
        this.operacaoDecisao = event.target.value;
    };

    handleChangeOperacaoObservacao = (event) => {
        this.operacaoObservacao = event.target.value;
    };

    isStringPreenchida(string) {
        return string && (string + '').trim() !== '';
    }

    getAlertasProcesso = (registro) => {
        let alertas = [];

        if (registro.certidaoGerada) {
            alertas.push({
                mensagem: 'Não é possível alterar o rascunho caso a certidão já tenha sido gerada.',
                impeditivo: true
            });
        }

        return alertas;
    };

    render() {
        return (
            <Fragment>
                <div
                    style={{ marginRight: '10px', paddingLeft: '5px' }}
                    className="corBotao"
                >
                    <Tooltip
                        title={'Alterar rascunho da certidão em lote'}
                        placement="top-start"
                    >
                        <IconButton
                            aria-label="Alterar rascunho da certidão em lote"
                            onClick={this.handleClickIcone}
                            sx={{ color: 'inherit', padding: '4px' }}
                        >
                            <EditNote style={{ width: '24px', height: '24px' }} />
                        </IconButton>
                    </Tooltip>
                </div>
                {this.dialogListagemOpen && (
                    <Dialog
                        open
                        fullWidth
                        maxWidth="xl"
                        onClose={this.handleCloseDialog}
                        PaperProps={{ style: { height: '100%' } }}
                    >
                        <DialogTitle>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        flexGrow: 1
                                    }}
                                >
                                    <h4 style={{ margin: '0px' }}>Alterar rascunho da certidão em lote</h4>
                                    <div style={{ margin: '0px 10px' }}>
                                        <NumeroProcesso
                                            dense
                                            tooltip="Número do processo a ser pesquisado na listagem"
                                            callbackOnChange={this.callbackNumeroProcesso}
                                            clickLimpar={this.limparProcessoPesquisa}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                margin: '10px',
                                                paddingTop: '5px'
                                            }}
                                        >
                                            <PainelFiltros />
                                        </div>
                                        <FiltroRapido />
                                    </div>
                                </div>
                            </div>
                            {this.renderRascunho}
                        </DialogTitle>
                        {this.renderConteudo}
                    </Dialog>
                )}
            </Fragment>
        );
    }

    get renderRascunho() {
        return (
            <div style={{ marginTop: '15px' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        margin: '3px 0px',
                        paddingLeft: '10px',
                        flexDirection: 'column'
                    }}
                >
                    <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                        <Typography
                            variant="subtitle1"
                            color="primary"
                            style={{ paddingRight: '15px', whiteSpace: 'nowrap', textAlign: 'center' }}
                        >
                            <b>Rascunho</b>
                        </Typography>
                        <TextField
                            id={'rascunho'}
                            value={this.rascunho}
                            multiline={true}
                            onChange={this.handleRascunho}
                            variant="outlined"
                            fullWidth={true}
                            size={'small'}
                            maxRows={5}
                            minRows={5}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', mt: 1 }}>
                        <AutoTexto
                            opcoes={this.opcoesAutoTexto}
                            onIncluirOpcaoSelecionada={this.handleIncluirOpcaoSelecionada}
                            estiloAutoComplete={{ minWidth: '200px' }}
                        />
                    </Box>
                </Box>
            </div>
        );
    }

    get renderConteudo() {
        return this.sessaoStore.carregandoProcessos ? (
            <div style={{ display: 'flex' }}>
                <CircularProgress
                    disableShrink
                    style={{ margin: '20px' }}
                />
                <Typography
                    variant="h6"
                    style={{ margin: '25px 10px' }}
                >
                    Carregando processos...
                </Typography>
            </div>
        ) : (
            <Fragment>
                <OperacoesEmLoteSelecionar
                    processosSelecionaveis={this.processosSelecionaveis}
                    processos={this.processos}
                />
                <PainelProcessosOperacoesEmLote
                    processos={this.processos}
                    mensagemLoteSemProcessos={this.mensagemLoteSemProcessos}
                    alertasProcessos={this.alertasProcessos}
                />
                <DialogActions>
                    <Button
                        onClick={this.handleCloseDialog}
                        color="secondary"
                    >
                        Fechar
                    </Button>
                    <Tooltip
                        placement="top"
                        title={
                            'Selecione os processos e informe o novo texto que deve ser inserido no rascunho da certidão desses processos.'
                        }
                    >
                        <span>
                            <Button
                                onClick={this.handleOpenDialogConfirmacao}
                                color="primary"
                                disabled={this.desabilitarAlteracao}
                            >
                                Confirmar
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>

                <ModalConfirmacao
                    open={this.dialogConfirmacaoOpen}
                    titulo="Confirmação de alteração de rascunho em lote"
                    tipoConfirmacao={TIPO_CONFIRMACAO.ATUALIZACAO}
                    callbackCancelar={this.handleCloseDialogConfirmacao}
                    callbackConfirmar={this.handleConfirmarEnvioRascunho}
                    textoBotaoConfirmacao="Confirmar"
                    textoBotaoCancelamento="Cancelar"
                >
                    <Typography>Qual o tipo de operação deseja realizar?</Typography>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="primary"><b>Texto da Decisão:</b></Typography>
                        <FormControl>
                            <RadioGroup
                                value={this.operacaoDecisao}
                                onChange={this.handleChangeOperacaoDecisao}
                            >
                                <FormControlLabel
                                    value="MANTER"
                                    control={<Radio />}
                                    label="Manter decisão atual"
                                />
                                <FormControlLabel
                                    value="ATUALIZAR"
                                    control={<Radio />}
                                    label="Atualizar decisão (buscar do julgamento)"
                                />
                                <FormControlLabel
                                    value="REMOVER"
                                    control={<Radio />}
                                    label="Remover decisão"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="primary"><b>Observações:</b></Typography>
                        <FormControl>
                            <RadioGroup
                                value={this.operacaoObservacao}
                                onChange={this.handleChangeOperacaoObservacao}
                            >
                                <FormControlLabel
                                    value="SUBSTITUIR"
                                    control={<Radio />}
                                    label="Substituir observações pelo texto informado"
                                />
                                <FormControlLabel
                                    value="ADICIONAR"
                                    control={<Radio />}
                                    label="Adicionar texto informado às observações existentes"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                </ModalConfirmacao>
            </Fragment>
        );
    }
}

export default inject('stores')(observer(RascunhoCertidaoEmLote));
